import { inject, Injectable, linkedSignal } from '@angular/core';
import { Router } from '@angular/router';
import { injectOrg } from '@quipex/core/auth';
import { qpxToast } from '@quipex/core/utils';
import type {
    QuippyHomeActions,
    QuippyScope,
} from '@quipex/feature/quippy-conversation';

export type QuippyPage = 'home' | 'chat';

export function scopeRoute(page: QuippyPage, target: QuippyScope): string[] {
    if (target.kind === 'portfolio')
        return page === 'home' ? ['/'] : ['/chat', 'new'];
    return page === 'home'
        ? ['/buildings', target.buildingId]
        : ['/buildings', target.buildingId, 'chat', 'new'];
}

@Injectable()
export class QuippyScopeNavigation implements QuippyHomeActions {
    private readonly router = inject(Router);
    private readonly org = injectOrg();
    private readonly drafts = linkedSignal({
        source: () => this.org()?.id,
        computation: () => new Map<string, string>(),
    });

    public draft(target: QuippyScope): string {
        return this.drafts().get(targetKey(target)) ?? '';
    }

    public setDraft(target: QuippyScope, text: string): void {
        this.drafts.update((drafts) => {
            const next = new Map(drafts);
            if (text) next.set(targetKey(target), text);
            else next.delete(targetKey(target));
            return next;
        });
    }

    public async navigate(
        page: QuippyPage,
        target: QuippyScope
    ): Promise<boolean> {
        const commands = scopeRoute(page, target);
        const destination = this.router.createUrlTree(commands);
        if (
            this.router.isActive(destination, {
                paths: 'exact',
                queryParams: 'ignored',
                fragment: 'ignored',
                matrixParams: 'ignored',
            })
        )
            return true;
        try {
            const navigated = await this.router.navigate(commands);
            if (!navigated)
                qpxToast.error('Unable to change building scope. Try again.');
            return navigated;
        } catch {
            qpxToast.error('Unable to change building scope. Try again.');
            return false;
        }
    }

    public navigateHome(target: QuippyScope): Promise<boolean> {
        return this.navigate('home', target);
    }
}

export function injectQuippyScopeNavigation(): QuippyScopeNavigation {
    return inject(QuippyScopeNavigation);
}

function targetKey(target: QuippyScope): string {
    return target.kind === 'portfolio'
        ? 'portfolio'
        : `building:${target.buildingId}`;
}
